import { useState } from "react";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import {
  CouponType,
  NormasTeachingCouponDTO,
} from "../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { NormasTeachingItemType } from "../Stripe/StripeOrderDTO.ts";
import { OutlinedButton, TextInput } from "@normasteaching/norma-ui";

type CouponSectionProps = {
  onCouponApplied: (coupon: NormasTeachingCouponDTO) => void;
  currentCoupon?: NormasTeachingCouponDTO;
  onCouponRemoved: () => void;
  allCoupons: NormasTeachingCouponDTO[];
  isLoadingCoupons: boolean;
  itemId: string;
  itemType: NormasTeachingItemType;
  validityCheckDate?: Date;
};

function isCouponApplicableToItem(
  coupon: NormasTeachingCouponDTO,
  itemId: string,
  itemType: NormasTeachingItemType,
): boolean {
  if (!coupon.applicableProducts || coupon.applicableProducts.length === 0) {
    return true;
  }

  return coupon.applicableProducts.some(
    (ap) => ap.id === itemId && ap.itemType === itemType,
  );
}

export const CouponSection = ({
  onCouponApplied,
  currentCoupon,
  onCouponRemoved,
  allCoupons,
  isLoadingCoupons,
  itemId,
  itemType,
  validityCheckDate,
}: CouponSectionProps) => {
  const [couponInput, setCouponInput] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateCoupon = (
    coupon: NormasTeachingCouponDTO,
  ): { valid: boolean; error?: string } => {
    if (!coupon.isActive) {
      return { valid: false, error: "Coupon non valido" };
    }

    if (!coupon.isUnlimitedUses && coupon.maxNumberOfUses <= 0) {
      return {
        valid: false,
        error: "Coupon non valido",
      };
    }

    const now = validityCheckDate ?? new Date();

    if (coupon.validFrom && new Date(coupon.validFrom) > now) {
      return { valid: false, error: "Questo coupon non è ancora valido" };
    }

    if (coupon.validTo && new Date(coupon.validTo) < now) {
      return { valid: false, error: "Coupon scaduto" };
    }

    if (!isCouponApplicableToItem(coupon, itemId, itemType)) {
      return {
        valid: false,
        error: "Questo coupon non è applicabile a questo prodotto",
      };
    }

    return { valid: true };
  };

  const handleApplyCoupon = () => {
    setValidationError(null);

    if (!couponInput.trim()) {
      return;
    }

    const inputCode = couponInput.trim().toUpperCase();

    const foundCoupon = allCoupons.find(
      (c) => c.couponCode.toUpperCase() === inputCode,
    );

    if (!foundCoupon) {
      setValidationError("Coupon non trovato o non valido");
      return;
    }

    const validation = validateCoupon(foundCoupon);

    if (!validation.valid) {
      setValidationError(validation.error!);
      return;
    }

    onCouponApplied(foundCoupon);
    setCouponInput("");
    setValidationError(null);
  };

  const handleRemoveCoupon = () => {
    onCouponRemoved();
    setCouponInput("");
    setValidationError(null);
  };

  const getCouponDisplayText = (coupon: NormasTeachingCouponDTO): string => {
    if (coupon.value.couponType === CouponType.Percentage) {
      return `Sconto del ${coupon.value.amount}%`;
    } else {
      return `Sconto di €${(coupon.value.amount / 100).toFixed(2)}`;
    }
  };

  return (
    <div className="pt-6 border-t">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Codice coupon</h3>

      {/* Loading State */}
      {isLoadingCoupons && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <p className="text-sm text-blue-800">
              Caricamento coupon disponibili...
            </p>
          </div>
        </div>
      )}

      {/* Applied NormasTeachingCoupon Display */}
      {currentCoupon && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 rounded-full p-2">
                <Check className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-blue-900">
                  Coupon applicato: {currentCoupon.couponCode}
                </p>
                <p className="text-sm text-blue-700">
                  {getCouponDisplayText(currentCoupon)}
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveCoupon}
              className="text-blue-600 hover:text-blue-800 transition-colors"
              aria-label="Rimuovi coupon"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* NormasTeachingCoupon Input */}
      <div className="space-y-3">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-9">
            <TextInput
              type="text"
              value={couponInput}
              onChange={(e) => {
                setCouponInput(e.target.value.toUpperCase());
                setValidationError(null);
              }}
              onKeyPress={(e) => e.key === "Enter" && handleApplyCoupon()}
              placeholder="Inserisci il tuo Codice Promozionale"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoadingCoupons}
            ></TextInput>
          </div>
          <div className="col-span-12 md:col-span-3">
            <OutlinedButton
              onClick={handleApplyCoupon}
              disabled={!couponInput.trim() || isLoadingCoupons}
              label="Applica"
            ></OutlinedButton>
          </div>
        </div>

        {/* Error Message */}
        {validationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{validationError}</p>
            </div>
          </div>
        )}

        <p className="text-sm text-gray-600">
          Hai un codice sconto? Inseriscilo qui per ottenere il tuo sconto
        </p>
      </div>
    </div>
  );
};
